import React from "react";
import * as io from "io-ts";
import createGlobalState from "@ui/lib/createGlobalState";
import { buildOptions, FilterWrapper, NONE_LABEL, useSubStateSetter } from "@ui/Filter";
import FilterSelect from "@ui/Filter/FilterSelect";
import t from "../../../../../../../i18n";
import { useTranslations } from "../../../../../../../api/useTranslations";
import Reset from "@ui/Filter/Reset";
import {
  DenormalizedEventStatus,
  getEventStatusText,
  sortedDenormalizedEventStatus,
} from "../../../../utils/faultEventStatus";
import classNames from "classnames";
import { uniqBy } from "diagnostics-lib/array";

const FilterValues = io.intersection([
  io.strict({
    projectType: io.array(io.string),
    projectSite: io.array(io.string),
    eventStatus: io.array(DenormalizedEventStatus),
  }),
  io.partial({
    detection: io.array(io.string),
  }),
]);
type FilterValues = io.TypeOf<typeof FilterValues>;
const DEFAULT_FILTER_VALUES: FilterValues = {
  projectType: [],
  projectSite: [],
  eventStatus: [],
  detection: [],
};
export const useEventHistoryFilter = createGlobalState({
  storage: "session",
  key: "useEventHistoryFilter",
  type: FilterValues,
  defaultValue: DEFAULT_FILTER_VALUES,
  scoped: true,
});

interface P {
  className?: string;
  projectTypes: string[];
  projectSites: string[];
  filterValues: FilterValues;
  setFilterValues: (props: React.SetStateAction<FilterValues>) => void;
  showDetection?: boolean;
  defaultFilterValues?: FilterValues;
}

const Filters = ({
  className,
  projectTypes,
  projectSites,
  filterValues,
  setFilterValues,
  defaultFilterValues = DEFAULT_FILTER_VALUES,
  showDetection = true,
}: P) => {
  const translations = useTranslations();
  const setDetectionValues = useSubStateSetter(setFilterValues, "detection");

  const projectTypeOptions = React.useMemo(() => {
    const allOptions = projectTypes.map(typeId => ({
      label: typeId ?? NONE_LABEL,
      value: typeId ?? null,
    }));
    return buildOptions(allOptions);
  }, [projectTypes]);

  const projectSiteOptions = React.useMemo(() => {
    const allOptions = projectSites.map(siteId => ({
      label: siteId ?? NONE_LABEL,
      value: siteId ?? null,
    }));
    return buildOptions(allOptions);
  }, [projectSites]);

  const detectionOptions = React.useMemo(() => {
    const allOptions = [true, false].map(detection => ({
      label: t("project.event.side_panel.history.filter.detection.label", {
        label: detection,
      }),
      value: detection.toString(),
    }));
    return uniqBy(allOptions, "value");
  }, []);

  const faultEventStatusOptions = React.useMemo(
    () =>
      uniqBy(
        sortedDenormalizedEventStatus
          .filter(status => status !== "TO_BE_ANALYSED")
          .map(v => ({
            label: String(getEventStatusText(v)),
            value: v,
          })),
        "value",
      ),
    [],
  );

  return (
    <FilterWrapper className={classNames(className, { wrappable: true })}>
      <FilterSelect<string>
        title={t("administration.quantities.table.header.project_type", translations)}
        options={projectTypeOptions}
        filtered
        values={filterValues["projectType"]}
        setValues={useSubStateSetter(setFilterValues, "projectType")}
      />
      <FilterSelect<string>
        title="Site"
        options={projectSiteOptions}
        filtered
        values={filterValues["projectSite"]}
        setValues={useSubStateSetter(setFilterValues, "projectSite")}
      />
      <FilterSelect<DenormalizedEventStatus>
        title={t("project.event.side_panel.history.filter.fault_event_status")}
        options={faultEventStatusOptions}
        values={filterValues["eventStatus"]}
        setValues={useSubStateSetter(setFilterValues, "eventStatus")}
      />
      {showDetection ? (
        <FilterSelect<string>
          title={t("project.event.side_panel.history.filter.detection")}
          options={detectionOptions}
          values={filterValues["detection"] ?? []}
          setValues={setDetectionValues}
        />
      ) : null}
      <Reset
        filterValues={filterValues}
        setFilterValues={setFilterValues}
        defaultFilterValues={defaultFilterValues}
      />
    </FilterWrapper>
  );
};
export default Filters;
